






















































































































































import { namespace } from "vuex-class";
import { Component, Ref, Vue } from "vue-property-decorator";
import PreviousPageArrow from "@/components/PreviousPageArrow.vue";
import BackOfLicenseSvg from "@/modules/license/components/BackOfLicenseSvg.vue";
import BackOfLicenseDesktopSvg from "@/modules/license/components/BackOfLicenseDesktopSvg.vue";
import Loader from "@/components/svg/Loader.vue";
import { base64toBlob } from "@/utils/index";
import {
  DocumentSide,
  DocumentType,
  UploadPayload,
} from "@/modules/document/types";
import heic2any from "heic2any";
import axios from "axios";

const Document = namespace("document");

@Component({
  metaInfo() {
    return {
      title: "Orba One | Take Your License",
    };
  },
  components: {
    "back-of-license-svg": BackOfLicenseSvg,
    "back-of-license-desktop-svg": BackOfLicenseDesktopSvg,
    "previous-page-arrow": PreviousPageArrow,
    loader: Loader,
  },
})
export default class CaptureBackLicense extends Vue {
  @Document.Action("upload") upload!: (payload: UploadPayload) => Promise<{}>;
  @Document.Getter("getCountryCode") countryCode!: string;
  @Ref() previewDocument!: HTMLImageElement;

  base64Image = "";
  isImageLoaded = false;
  isUploading = false;
  isUploadingError = false;

  // used for the red toast below the upload button (and reused for invalid file errors)
  isValidationError = false;
  errorMessage = "";

  imageBlob?: Blob;

  async mounted() {
    try {
      if (Vue.prototype.$useAudioInstructions) {
        Vue.prototype.$audioConfigs.stopLicenceFrontReadability?.();
        await Vue.prototype.$audioConfigs.playLicenceBack?.();
      }
    } catch (err) {
      console.warn("Audio playback failed:", err);
    }
  }

  /**
   * previewImage:
   * - restrict PDFs / unsupported types
   * - preview supported images only
   * - use error toast (errorMessage + isValidationError) for problems
   */
  async previewImage(files: File[] | FileList) {
    const file: File = files && files[0];
    if (!file) return;

    const fileName = (file.name || "").toLowerCase();
    const fileType = file.type || "";

    // Reset previous errors
    this.isValidationError = false;
    this.errorMessage = "";

    // Block PDFs explicitly
    if (fileType === "application/pdf" || fileName.endsWith(".pdf")) {
      this.showErrorToast(
        "PDF files are not supported. Please upload a JPG/PNG/HEIC image."
      );
      return;
    }

    // If browser reports a MIME type and it's not image/*, reject it
    if (fileType && !fileType.startsWith("image/")) {
      this.showErrorToast(
        "Unsupported file type. Please upload a JPG/PNG/HEIC image."
      );
      return;
    }

    // Helper to read a Blob (file or converted blob) into base64 and set state
    const readBlobToPreview = (blob: Blob) =>
      new Promise<void>((resolve, reject) => {
        const r = new FileReader();
        r.onload = async (ev) => {
          try {
            if (ev && ev.target) {
              this.base64Image = ev.target.result as string;
              this.imageBlob = await base64toBlob(this.base64Image);
              this.isImageLoaded = true;
              // clear any previous validation error
              this.isValidationError = false;
              this.errorMessage = "";
              resolve();
            } else {
              reject(new Error("FileReader returned no result"));
            }
          } catch (err) {
            reject(err);
          }
        };
        r.onerror = (e) => reject(e);
        r.readAsDataURL(blob);
      });

    // Detect HEIC/HEIF either by MIME or filename extension
    const isHeic =
      fileType === "image/heic" ||
      fileType === "image/heif" ||
      fileName.endsWith(".heic") ||
      fileName.endsWith(".heif");

    if (isHeic) {
      // Convert HEIC -> JPEG client-side
      try {
        // heic2any returns a Blob (or an Array of Blobs)
        const convertedBlob = (await heic2any({
          blob: file as Blob,
          toType: "image/jpeg",
          quality: 0.9,
        })) as Blob;

        // Some builds may return an array (heic2any v0.?) — handle both:
        const blobToUse = Array.isArray(convertedBlob)
          ? convertedBlob[0]
          : convertedBlob;

        await readBlobToPreview(blobToUse);
      } catch (err) {
        console.error("HEIC conversion failed:", err);
        this.showErrorToast(
          "Failed to convert HEIC image. Please try again or use a JPEG/PNG."
        );
        return;
      }
    } else {
      // Non-HEIC image: read directly
      try {
        await readBlobToPreview(file);
      } catch (err) {
        console.error("Failed to read file for preview:", err);
        this.showErrorToast(
          "Cannot preview this file. Please try a different image."
        );
        return;
      }
    }
  }

  /**
   * Reusable toast function that uses the same UI as the "Invalid ID" message.
   * message: string displayed to user
   */
  showErrorToast(message: string) {
    this.errorMessage = message;
    this.isValidationError = true;

    // Auto-hide after 4s
    setTimeout(() => {
      this.isValidationError = false;
      this.errorMessage = "";
    }, 5000);
  }

  retryImage() {
    this.isImageLoaded = false;
    this.isValidationError = false;
    this.errorMessage = "";
  }

  confirmImage() {
    this.uploadNationalFront();
  }

  uploadNationalFront(): void {
    const params = new URLSearchParams(window.location.search);
    const publicKey = params.get("publicKey");
    const applicantId = params.get("applicantId");

    if (!publicKey || !applicantId) return;

    const formData = new FormData();
    formData.append("applicantId", applicantId);
    formData.append("publicKey", publicKey);
    formData.append("type", DocumentType.DRIVERSLICENSE);
    formData.append("side", DocumentSide.BACK);
    formData.append("countryCode", this.countryCode);

    if (this.imageBlob) {
      formData.append(
        "file",
        this.imageBlob,
        `license_back_${applicantId}_${publicKey}.png`
      );
    }

    this.isUploading = true;
    this.isUploadingError = false;

    this.upload(formData)
      .then(() => {
        if (Vue.prototype.$useAudioInstructions) {
          Vue.prototype.$audioConfigs.stopLicenceBack?.();
          Vue.prototype.$audioConfigs.playUploadIsCompleted?.();
        }

        this.isUploading = false;
        this.$router.push({ name: "finish", query: this.$route.query });
      })
      .catch(() => {
        this.isValidationError = true;
        this.isUploading = false;
        this.isUploadingError = true;

        if (Vue.prototype.$useAudioInstructions) {
          Vue.prototype.$audioConfigs.stopLicenceBack?.();
          Vue.prototype.$audioConfigs.playLicenceBackFailed?.();
        }
      });
  }

  isMobile(): boolean {
    return screen.width <= 760;
  }
  uploadLicenceBackStep() {
    const params = new URLSearchParams(window.location.search);
    const publicKey = params.get("publicKey");
    const applicantId = params.get("applicantId");
    const magicLink = params.get("magic");
    const progressionStep = 13;

    if ((publicKey && applicantId) || magicLink) {
      axios.put(
        `${process.env.VUE_APP_API_URL}/applicants/${applicantId}/verification-progress-step`,
        {
          publicKey,
          progressionStep,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
    }
  }
}
